// ═══════════════════════════════════════════════════════
// ULTIMAZE TOWER WARS — CapacitorPushAdapter
// Phase 4: Capacitor Push + Local Notifications
// ═══════════════════════════════════════════════════════

import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import type { INotificationService } from '../interfaces';
import { ENV } from '../config/env.js';

/** Derive HTTP base URL from the WS URL (ws:// → http://, wss:// → https://). */
function httpBase(): string {
  return ENV.WS_SERVER_URL.replace(/^wss?:\/\//, (m) =>
    m.startsWith('wss') ? 'https://' : 'http://'
  ).replace(/\/$/, '')
}

export class CapacitorPushAdapter implements INotificationService {
  /** FCM/APNs registration token, populated after requestPermission(). */
  private registrationToken: string | null = null

  /**
   * Returns the FCM (Android) or APNs (iOS) device token received after
   * a successful PushNotifications.register() call.  Will be null until
   * requestPermission() has been called and the OS/Firebase has provided a token.
   */
  getToken(): string | null {
    return this.registrationToken
  }

  async isAvailable(): Promise<boolean> {
    return Capacitor.isNativePlatform();
  }

  async requestPermission(): Promise<boolean> {
    try {
      const [localResult, pushResult] = await Promise.all([
        LocalNotifications.requestPermissions(),
        PushNotifications.requestPermissions(),
      ]);

      const granted = localResult.display === 'granted' && pushResult.receive === 'granted';

      if (granted) {
        // Register listener for successful token delivery BEFORE calling register().
        await PushNotifications.addListener('registration', (token) => {
          this.registrationToken = token.value
          if (ENV.IS_DEV) {
            console.log('[CapacitorPushAdapter] FCM/APNs token received:', token.value)
          }
          // Send token to server so it can deliver push notifications to this device.
          void this._registerTokenWithServer(token.value)
        })

        // Register listener for registration errors.
        await PushNotifications.addListener('registrationError', (error) => {
          console.error('[CapacitorPushAdapter] Push registration error:', error)
        })

        // Trigger OS-level push registration — delivers token to the 'registration' listener.
        await PushNotifications.register()
      }

      return granted;
    } catch (err) {
      console.warn('[CapacitorPushAdapter] requestPermission failed:', err);
      return false;
    }
  }

  /**
   * POST the device token to the server so it can send push notifications.
   * Reads the JWT access token from localStorage (set by useAuthStore on login).
   * Silently ignores errors — push is best-effort.
   */
  private async _registerTokenWithServer(token: string): Promise<void> {
    try {
      const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android'
      const accessToken = localStorage.getItem('ultimaze_access_token')
      if (!accessToken) return // user not logged in yet; retry happens after next login

      await fetch(`${httpBase()}/api/push/register-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ token, platform }),
      })
    } catch {
      // Network error — will retry on next app launch when token is re-delivered.
    }
  }

  async scheduleLocal(opts: {
    title: string;
    body: string;
    atDate: Date;
  }): Promise<void> {
    try {
      // Derive a stable numeric id from title length and atDate ms value.
      // Capacitor LocalNotifications requires a numeric id.
      const id = (opts.title.length * 1_000_000 + (opts.atDate.getTime() % 1_000_000)) >>> 0;

      await LocalNotifications.schedule({
        notifications: [
          {
            id,
            title: opts.title,
            body: opts.body,
            schedule: { at: opts.atDate },
            smallIcon: 'ic_notification',
            sound: undefined,
            attachments: undefined,
            actionTypeId: '',
            extra: null,
          },
        ],
      });
    } catch (err) {
      console.warn('[CapacitorPushAdapter] scheduleLocal failed:', err);
    }
  }

  async cancelAll(): Promise<void> {
    try {
      const pending = await LocalNotifications.getPending();
      if (pending.notifications.length > 0) {
        await LocalNotifications.cancel({ notifications: pending.notifications });
      }
    } catch (err) {
      console.warn('[CapacitorPushAdapter] cancelAll failed:', err);
    }
  }
}
